import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import {
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import {
  createDevelopmentPaceScenarioSearchAtom,
  type DevelopmentPaceScenarioParams,
} from './atoms';
import type { DevelopmentPaceScenarioResult } from './types';
import { DevelopmentPaceScenariosFilters } from './development-pace-scenarios-filters';
import {
  DevelopmentPaceSummaryCards,
  type SummaryCardType,
} from './summary-cards';
import {
  DEFAULT_PARAMS,
  PACE_MODEL_LABELS,
  SORT_FIELD_MAP,
  columnFiltersToParams,
} from './utils';
import styles from './development-pace-scenarios-content.module.scss';

// ─────────────────────────────────────
// Column Definitions
// ─────────────────────────────────────

type DpsRow = DevelopmentPaceScenarioResult & Record<string, unknown>;

const columns: DataTableColumnDef<DpsRow>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 280,
    cell: ({ row }) => <Link to={row.original.href}>{row.original.name}</Link>,
  },
  {
    accessorKey: 'paceModel',
    header: 'Scheduling Model',
    size: 200,
    meta: { type: 'enum' },
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return value
        ? (PACE_MODEL_LABELS[value as keyof typeof PACE_MODEL_LABELS] ?? value)
        : '--';
    },
  },
  {
    accessorKey: 'areaCount',
    header: '# of Areas',
    size: 120,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    size: 160,
    meta: { type: 'enum' },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created Date',
    size: 180,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated Date',
    size: 180,
    meta: { type: 'date' },
  },
];

const initialTableState: InitialTableState = {
  columnPinning: { left: ['name'] },
  sortColumn: 'createdAt',
  sortDirection: 'desc',
};

// ─────────────────────────────────────
// Content Component
// ─────────────────────────────────────

const DevelopmentPaceScenariosContent: FC = () => {
  const { searchAtom } = useMemo(
    () => createDevelopmentPaceScenarioSearchAtom(),
    []
  );
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [params, setParams] =
    useState<DevelopmentPaceScenarioParams>(DEFAULT_PARAMS);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const updateParams = useCallback(
    (updates: Partial<DevelopmentPaceScenarioParams>) => {
      setParams((current) => ({ ...current, page: 1, ...updates }));
    },
    []
  );

  const handleColumnFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      updateParams(columnFiltersToParams(filters));
    },
    [updateParams]
  );

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        updateParams({ sortAttribute: 'created_at', sortDirection: 'DESC' });
      } else {
        const sort = sorting[0];
        updateParams({
          sortAttribute: SORT_FIELD_MAP[sort.id] ?? sort.id,
          sortDirection: sort.desc ? 'DESC' : 'ASC',
        });
      }
    },
    [updateParams]
  );

  const handleGlobalSearchChange = useCallback(
    (search: string) => {
      const newText = search || undefined;
      if (params.text === newText) return;
      updateParams({ text: newText });
    },
    [params.text, updateParams]
  );

  const handleSummaryCardClick = useCallback(
    (card: SummaryCardType) => {
      const now = moment();
      const daysBack = { last24h: 1, last7d: 7, lastMonth: 30 };
      const start = now.clone().subtract(daysBack[card], 'days');
      updateParams({
        createdAt: {
          start: start.format('YYYY-MM-DD'),
          end: now.format('YYYY-MM-DD'),
        },
      });
    },
    [updateParams]
  );

  const results = searchResult.data?.results ?? [];
  const totalCount = searchResult.data?.totalCount ?? 0;

  const handleLoadData = useCallback(({ page, pageSize }: TableState) => {
    setParams((current) => ({ ...current, page, pageSize }));
  }, []);

  return (
    <div className={styles.contentWrapper}>
      <DevelopmentPaceSummaryCards
        aggregations={searchResult.data?.aggregations}
        onCardClick={handleSummaryCardClick}
      />
      <div className={styles.tableWrapper}>
        <DataTable<DpsRow>
          data={results as DpsRow[]}
          columns={columns as DataTableColumnDef<DpsRow>[]}
          getRowId={(row) => String(row.id)}
          initialTableState={initialTableState}
          persistenceKey="development-pace-scenarios-list-table"
          config={{ columnFilters: false, columnReordering: false }}
          isLoading={searchResult.loading}
          error={searchResult.error ?? null}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={handleLoadData}
          onSortChange={(s) => handleSortChange(s.sorting)}
          onGlobalSearchChange={(s) => handleGlobalSearchChange(s.globalFilter)}
          onFilterChange={(s) => handleColumnFilterChange(s.columnFilters)}
          searchPlaceholder="Search by Configuration Name"
          renderFilterPanel={() => (
            <DevelopmentPaceScenariosFilters
              facets={searchResult.data?.facets}
            />
          )}
        />
      </div>
    </div>
  );
};

export default DevelopmentPaceScenariosContent;
